import { VideoElementRenderer, VideoRendererHost } from './video-renderer';

interface Go2rtcMessage {
  type: string;
  value?: string;
}

// Offered to go2rtc in preference order, filtered to what this browser decodes.
const CANDIDATE_CODECS: readonly string[] = [
  'avc1.640029',
  'avc1.64002A',
  'avc1.640033',
  'avc1.4d002a',
  'hvc1.1.6.L153.B0'
];

// iPhone exposes no MediaSource at all; WebKit ships ManagedMediaSource in its
// place, which is attached through srcObject instead of an object URL.
interface MediaSourceFactory {
  new(): MediaSource;
  isTypeSupported(type: string): boolean;
}

const MAX_LIVE_DELAY_S: number = 1.5;  // drift past this and we jump to the edge
const BUFFER_KEEP_S: number = 10;      // history retained behind the playhead

// Fallback for clients that cannot reach the WebRTC media port: the same stream
// as fragmented MP4 over a WebSocket, on the HTTPS port the page came from.
// That is why no TURN server is needed.
export class MseRenderer extends VideoElementRenderer {
  private socket: WebSocket | null = null;
  private mediaSource: MediaSource | null = null;
  private factory: MediaSourceFactory | null = null;
  private managed: boolean = false;
  private sourceBuffer: SourceBuffer | null = null;
  private readonly queue: ArrayBuffer[] = [];
  private stopped: boolean = false;

  private readonly onBufferUpdate = (): void => {
    if (!this.trimWhilePaused()) {
      this.flush();
    }
  };
  private readonly onTimeUpdate = (): void => this.keepUpWithLive();

  constructor(video: HTMLVideoElement, host: VideoRendererHost, private readonly wsUrl: string) {
    super(video, host);
  }

  public start(): void {
    const managed: MediaSourceFactory | undefined =
      (window as unknown as { ManagedMediaSource?: MediaSourceFactory }).ManagedMediaSource;
    const factory: MediaSourceFactory | undefined =
      typeof MediaSource === 'undefined' ? managed : MediaSource;

    if (!factory) {
      this.host.onFatal('MediaSource not available');
      return;
    }

    this.factory = factory;
    this.managed = factory === managed;

    const supported: string[] = CANDIDATE_CODECS.filter(
      codec => factory.isTypeSupported(`video/mp4; codecs="${codec}"`)
    );

    if (!supported.length) {
      this.host.onFatal('no MSE codec supported');
      return;
    }

    this.video.muted = true;
    this.video.playsInline = true;
    this.observeVideo();
    this.video.addEventListener('timeupdate', this.onTimeUpdate);

    const socket = new WebSocket(this.wsUrl);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;

    socket.onopen = (): void => socket.send(JSON.stringify({ type: 'mse', value: supported.join(',') }));
    socket.onmessage = (event: MessageEvent): void => this.onMessage(event);
    socket.onerror = (): void => this.fail('websocket error');
    socket.onclose = (): void => this.fail('websocket closed');
  }

  private onMessage(event: MessageEvent): void {
    if (this.stopped) {
      return;
    }

    // Binary frames are fMP4 segments, text frames control messages.
    if (event.data instanceof ArrayBuffer) {
      this.queue.push(event.data);
      this.flush();
      return;
    }

    let message: Go2rtcMessage;
    try {
      message = JSON.parse(event.data as string) as Go2rtcMessage;
    } catch {
      return;
    }

    if (message.type === 'error') {
      this.fail(message.value || 'upstream error');
      return;
    }

    // The answer carries the MIME type go2rtc will actually send.
    if (message.type === 'mse' && message.value) {
      this.openMediaSource(message.value);
    }
  }

  private openMediaSource(mimeType: string): void {
    const factory: MediaSourceFactory | null = this.factory;

    if (!factory) {
      return;
    }

    const mediaSource: MediaSource = new factory();
    this.mediaSource = mediaSource;

    let objectUrl: string = '';

    if (this.managed) {
      // A ManagedMediaSource has no object URL, and stays closed while the
      // element is still allowed to hand playback to another device.
      this.video.disableRemotePlayback = true;
      this.video.srcObject = mediaSource;
    } else {
      objectUrl = URL.createObjectURL(mediaSource);
      this.video.src = objectUrl;
    }

    mediaSource.addEventListener('sourceopen', () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl); // the element keeps its own reference
      }

      if (this.stopped) {
        return;
      }

      try {
        // A live stream has no end. Left to follow the buffered length, the
        // duration lets WebKit's ManagedMediaSource fire `ended` as soon as
        // playback catches up with the last segment.
        mediaSource.duration = Infinity;
        const buffer: SourceBuffer = mediaSource.addSourceBuffer(mimeType);
        buffer.mode = 'segments';
        buffer.addEventListener('updateend', this.onBufferUpdate);
        this.sourceBuffer = buffer;
        this.flush();
        this.play();
      } catch (error: unknown) {
        this.fail(`addSourceBuffer: ${error instanceof Error ? error.message : 'failed'}`);
      }
    }, { once: true });
  }

  // One append per cycle: appendBuffer is async and updateend calls back here.
  private flush(): void {
    const buffer: SourceBuffer | null = this.sourceBuffer;

    if (!buffer || buffer.updating || !this.queue.length || this.stopped) {
      return;
    }

    const chunk: ArrayBuffer = this.queue.shift() as ArrayBuffer;

    try {
      buffer.appendBuffer(chunk);
    } catch (error: unknown) {
      this.fail(`appendBuffer: ${error instanceof Error ? error.message : 'failed'}`);
    }
  }

  // A throttled tab keeps buffering but stops rendering, ending up minutes
  // behind. Jump to the edge and drop history a live view never needs.
  private keepUpWithLive(): void {
    const buffered: TimeRanges = this.video.buffered;

    if (!buffered.length) {
      return;
    }

    const end: number = buffered.end(buffered.length - 1);
    if (end - this.video.currentTime > MAX_LIVE_DELAY_S) {
      this.video.currentTime = end - 0.3;
    }

    const start: number = buffered.start(0);
    const buffer: SourceBuffer | null = this.sourceBuffer;
    if (buffer && !buffer.updating && this.video.currentTime - start > BUFFER_KEEP_S) {
      try {
        buffer.remove(start, this.video.currentTime - BUFFER_KEEP_S);
      } catch {
        // Went back to updating between the two checks.
      }
    }
  }

  // Paused, the playhead stops and timeupdate with it while the stream keeps
  // coming: without this the buffer grows until QuotaExceededError. Only the
  // last seconds are kept, since play() resumes from the live edge anyway.
  private trimWhilePaused(): boolean {
    const buffer: SourceBuffer | null = this.sourceBuffer;
    const buffered: TimeRanges = this.video.buffered;

    if (!this.video.paused || !buffer || buffer.updating || !buffered.length) {
      return false;
    }

    const start: number = buffered.start(0);
    const end: number = buffered.end(buffered.length - 1);

    if (end - start <= 2 * BUFFER_KEEP_S) {
      return false;
    }

    try {
      buffer.remove(start, end - BUFFER_KEEP_S);
      return true;
    } catch {
      return false;
    }
  }

  public override play(): void {
    const buffered: TimeRanges = this.video.buffered;

    if (buffered.length) {
      this.video.currentTime = Math.max(buffered.start(0), buffered.end(buffered.length - 1) - 0.3);
    }
    super.play();
  }

  private fail(reason: string): void {
    if (this.stopped) {
      return;
    }
    this.stopped = true;
    this.host.onFatal(reason);
  }

  public override destroy(): void {
    this.stopped = true;
    this.queue.length = 0;

    this.video.removeEventListener('timeupdate', this.onTimeUpdate);

    if (this.socket) {
      // Drop the handlers first, or onclose reports a fatal for a teardown we
      // asked for.
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.onclose = null;
      try { this.socket.close(); } catch { /* already closed */ }
      this.socket = null;
    }

    if (this.sourceBuffer) {
      this.sourceBuffer.removeEventListener('updateend', this.onBufferUpdate);
      this.sourceBuffer = null;
    }

    if (this.mediaSource?.readyState === 'open') {
      try { this.mediaSource.endOfStream(); } catch { /* already ended */ }
    }
    this.mediaSource = null;

    super.destroy();
  }
}
